"use client";

import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from 'next-intl';

type SignInDialogProps = {
    open: boolean;
    onClose: () => void;
};

export function SignInDialog({ open, onClose }: SignInDialogProps) {
    const [account, setAccount] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const t = useTranslations('authErrors');
    const tAuth = useTranslations('auth');
    const tNav = useTranslations('nav');
    const apiBaseUrl = process.env.NEXT_PUBLIC_REDJOO_API_BASE_URL;
    const radjooUrl = process.env.NEXT_PUBLIC_RADJOO_URL;

    useEffect(() => {
        if (open) {
            setAccount("");
            setUsername("");
            setPassword("");
            setErrors([]);
            setIsLoading(false);
        }
    }, [open]);

    if (!open) return null; // don't render when closed

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setErrors([]);

        const usernameAccount = `${username}@${account}`;

        try {
            const response = await fetch(`${apiBaseUrl}/api/authenticate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: usernameAccount, // Format: user@account
                    password: password,
                }),
            });

            const data = await response.json();

            if (data.resultStatus === 'success') {
                // Convert ISO expiration to milliseconds for Flutter app
                const expirationDate = new Date(data.expiration);
                const exp = expirationDate.getTime().toString();

                // Construct the redirect URL with query parameters
                const redirectUrl = new URL(`${radjooUrl}`);
                redirectUrl.searchParams.append('accessToken', data.accessToken);
                redirectUrl.searchParams.append('refreshToken', data.refreshToken);
                redirectUrl.searchParams.append('exp', exp);

                // Redirect back to the Flutter app
                window.location.href = redirectUrl.toString();
            } else {
                const newErrors: string[] = [];
                const errorMsg = data.messages?.[0]?.toLowerCase() || '';

                if (errorMsg === 'user account has expired') {
                    newErrors.push(t('contact_admin'));
                    newErrors.push(t('account_expired'));
                } else if (errorMsg === 'user temporary locked') {
                    newErrors.push(t('user_temp_lock'));
                    newErrors.push(t('try_later', { minutes: 5 }));
                } else if (errorMsg === 'user regular locked') {
                    newErrors.push(t('user_reg_lock'));
                    newErrors.push(t('contact_admin'));
                } else if (errorMsg === 'user is disabled') {
                    newErrors.push(t('user_inactive'));
                    newErrors.push(t('contact_admin'));
                } else {
                    newErrors.push(t('invalid_userPass'));
                    console.log("---error " + (data.messages?.[0] || 'Unknown error'));
                }
                setErrors(newErrors);
            }
        } catch (err) {
            setErrors([t('generic_error')]);
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    if (typeof document === "undefined") return null;

    return createPortal(
        <div
            className="fixed inset-0 z-50 overflow-y-auto bg-black/40"
            aria-modal="true"
            role="dialog"
        >
            {/* click on backdrop closes dialog */}
            <div
                className="fixed inset-0"
                onClick={onClose}
            />

            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative z-10 w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl bg-white shadow-lg">
                {/* header */}
                <div className="flex items-center justify-between border-b px-6 py-4 mb-4">
                    <h2 className="text-base font-semibold text-gray-900">
                        {tAuth('title')}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1 hover:bg-gray-100"
                        aria-label={tNav('close')}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* body */}
                <form onSubmit={handleLogin} className="px-6 py-5 space-y-4">
                    {errors.length > 0 && (
                        <div className="bg-red-50 text-red-500 text-sm p-3 rounded-md border border-red-200">
                            <ul className="list-disc list-inside">
                                {errors.map((err, index) => (
                                    <li key={index}>{err}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="relative">
                        <input
                            id="account"
                            type="text"
                            required
                            value={account}
                            onChange={(e) => setAccount(e.target.value)}
                            onInvalid={(e) => e.currentTarget.setCustomValidity(t('required'))}
                            onInput={(e) => e.currentTarget.setCustomValidity('')}
                            className="peer block w-full rounded-lg border border-gray-300 bg-transparent px-3 pb-2.5 pt-4 text-sm text-gray-900"
                            placeholder=" "
                            dir="ltr"
                        />
                        <label
                            htmlFor="account"
                            className="absolute start-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:text-[var(--sm-teal-600)] rtl:peer-focus:translate-x-1/4"
                        >
                            {tAuth('account')}
                        </label>
                    </div>

                    <div className="relative">
                        <input
                            id="username"
                            type="text"
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            onInvalid={(e) => e.currentTarget.setCustomValidity(t('required'))}
                            onInput={(e) => e.currentTarget.setCustomValidity('')}
                            className="peer block w-full rounded-lg border border-gray-300 bg-transparent px-3 pb-2.5 pt-4 text-sm text-gray-900"
                            placeholder=" "
                            dir="ltr"
                        />
                        <label
                            htmlFor="username"
                            className="absolute start-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:text-[var(--sm-teal-600)] rtl:peer-focus:translate-x-1/4"
                        >
                            {tAuth('username')}
                        </label>
                    </div>

                    <div className="relative">
                        <input
                            id="password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onInvalid={(e) => e.currentTarget.setCustomValidity(t('required'))}
                            onInput={(e) => e.currentTarget.setCustomValidity('')}
                            className="peer block w-full rounded-lg border border-gray-300 bg-transparent px-3 pb-2.5 pt-4 text-sm text-gray-900"
                            placeholder=" "
                            dir="ltr"
                        />
                        <label
                            htmlFor="password"
                            className="absolute start-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:text-[var(--sm-teal-600)] rtl:peer-focus:translate-x-1/4"
                        >
                            {tAuth('password')}
                        </label>
                    </div>


                    <div className="flex gap-2 mt-10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            {tAuth('cancel')}
                        </button>
                        <button
                            type="submit"
                            className="flex-1 rounded-md bg-brand-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                            disabled={isLoading}
                        >
                            {isLoading ? '...' : tAuth('signIn')}
                        </button>
                    </div>
                </form>


                </div>
            </div>
        </div>,
        document.body
    );
}
